import { format } from "date-fns";
import { ROUTES, callAPI } from "../API/index";

// -----------------------------------------------
// Download Services
// -----------------------------------------------

export const getPdfDownload = async (type, params) => {
    /**
     * PDF 다운로드 통합 API
     * type: 다운로드 타입 (payment, leave, attendance, retirement-transfer, retirement, payment-year)
     * params: 각 타입에 맞는 파라미터들
     */
    return callAPI(ROUTES.PDF.DOWNLOAD, 'GET', {}, { type, ...params });
};

// 특정 타입별 PDF 다운로드 함수들 (편의를 위해)
export const getPaymentPdfDownload = (params) => {
    return getPdfDownload('payment', params);
};

export const getLeavePdfDownload = (params) => {
    return getPdfDownload('leave', params);
};

export const getAttendancePdfDownload = (params) => {
    return getPdfDownload('attendance', params);
};

export const getRetirementTransferPdfDownload = (params) => {
    return getPdfDownload('retirement-transfer', params);
};

export const getRetirementPdfDownload = (params) => {
    return getPdfDownload('retirement', params);
};

export const getPaymentYearPdfDownload = (params) => {
    return getPdfDownload('payment-year', params);
};

// -----------------------------------------------
// Excel Download Services
// -----------------------------------------------

export const downloadExcel = async (type, params, filename) => {
    /**
     * 엑셀 다운로드 통합 서비스
     * type: 다운로드 타입
     * params: API 파라미터
     * filename: 다운로드 파일명
     */
    try {
        console.log('엑셀 다운로드 요청:', {
            type: type,
            url: `/excel/download/${type}`,
            params: params,
            filename: filename
        });

        const response = await callAPI(ROUTES.EXCEL.DOWNLOAD, 'GET', {}, { type, ...params });

        console.log('엑셀 다운로드 응답:', {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            contentType: response.headers['content-type']
        });

        // 응답이 에러인지 확인 (서버에서 JSON 에러 응답을 보낸 경우)
        if (response.headers['content-type']?.includes('application/json')) {
            // Blob을 JSON으로 변환하여 에러 메시지 확인
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            if (errorData.error || errorData.message) {
                const error = new Error(errorData.message || errorData.error || '다운로드 중 오류가 발생했습니다.');
                error.response = { data: errorData, status: response.status };
                throw error;
            }
        }

        // 파일 다운로드 처리
        const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        
        // Blob 크기 확인 (너무 작으면 에러 응답일 가능성)
        if (blob.size < 100) {
            const text = await blob.text();
            console.warn('의심스러운 응답 크기:', blob.size, '내용:', text);
            try {
                const errorData = JSON.parse(text);
                if (errorData.error || errorData.message) {
                    const error = new Error(errorData.message || errorData.error || '다운로드 중 오류가 발생했습니다.');
                    error.response = { data: errorData, status: response.status };
                    throw error;
                }
            } catch (parseError) {
                // JSON 파싱 실패해도 계속 진행
            }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('엑셀 파일 다운로드 완료:', filename);
        return { response, filename };
    } catch (error) {
        console.error('엑셀 다운로드 오류:', {
            type: type,
            params: params,
            filename: filename,
            error: error,
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            responseData: error.response?.data
        });
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '엑셀 다운로드 중 오류가 발생했습니다.'));
    }
}

export const downloadPaymentExcel = (payYear, payMonth, paymentType, employeeNos, filename) => {
    /**
     * 급여 엑셀 다운로드
     */
    const params = { payYear, payMonth, paymentType, employeeNos };
    const downloadFilename = filename || `급여대장_${payYear}_${payMonth}_${paymentType}.xlsx`;
    return downloadExcel('payment', params, downloadFilename);
}

export const downloadLeaveExcel = (payYear, payMonth, paymentType, filename, baseDate) => {
    /**
     * 연차 엑셀 다운로드
     * @param {string} [baseDate] - 기준일 (yyyy-MM-dd), 일자 포함 조회·다운로드용
     */
    const params = { payYear, payMonth, paymentType };
    if (baseDate) {
        params.baseDate = baseDate;
        params.day = baseDate.split('-')[2];
    }
    const downloadFilename = filename || `연차조회_${payYear}_${payMonth}_${paymentType}.xlsx`;
    return downloadExcel('leave', params, downloadFilename);
}

export const downloadAttendanceExcel = (payYear, payMonth, employeeNos, filename) => {
    /**
     * 출퇴근 결과 조회 엑셀 다운로드
     */
    const params = { payYear, payMonth, employeeNos, filename };
    const downloadFilename = filename || `출퇴근결과조회_${payYear}_${payMonth}.xlsx`;
    return downloadExcel('attendance', params, downloadFilename);
}

export const downloadRetirementTransferExcel = (payYear, payMonth, emplNo, filename, transferData = {}) => {
    /**
     * 퇴직연금 이체의뢰서 엑셀 다운로드
     */
    const params = { payYear, payMonth, filename, transferData };
    if (emplNo) {
        params.employeeNos = emplNo;
    }
    const downloadFilename = filename || `퇴직연금이체의뢰서_${payYear}_${payMonth}.xlsx`;
    return downloadExcel('retirement-transfer', params, downloadFilename);
}

export const downloadRetirementExcel = (payYear, payMonth, filename, employeeNos = []) => {
    /**
     * 퇴직연금 엑셀 다운로드
     */
    const params = { payYear, payMonth, filename };
    if (employeeNos && employeeNos.length > 0) {
        params.employeeNos = employeeNos.join(',');
    }
    const downloadFilename = filename || `퇴직연금_${payYear}_${payMonth}.xlsx`;
    return downloadExcel('retirement', params, downloadFilename);
}

export const downloadPaymentYearExcel = (payYear, paymentType, employeeNos, filename) => {
    /**
     * 연간 급여지급급 엑셀 다운로드
     */
    const params = { payYear };
    
    // paymentType이 배열인 경우 쉼표로 구분된 문자열로 변환
    if (Array.isArray(paymentType) && paymentType.length > 0) {
        params.paymentType = paymentType.join(',');
    } else if (paymentType) {
        params.paymentType = paymentType;
    }
    
    if (employeeNos && employeeNos.length > 0) {
        params.employeeNos = employeeNos;
    }
    
    const downloadFilename = filename || `급여지급내역_${payYear}.xlsx`;
    return downloadExcel('payment-year', params, downloadFilename);
}

export const downloadDepartureExcel = (payYear, payMonth, employeeNos, filename) => {
    /**
     * 외국인 출국 엑셀 다운로드
     */
    const params = { payYear, payMonth, filename };
    const downloadFilename = filename || `출국만기보험_${payYear}_${payMonth}.xlsx`;
    return downloadExcel('departure', params, downloadFilename);
}

export const downloadTransferRequestExcel = (payYear, payMonth, paymentType, employeeNos, transferRequestData, filename) => {
    /**
     * 급여 이체의뢰서 엑셀 다운로드
     */
    const params = {
        payYear,
        payMonth,
        paymentType,
        employeeNos,
        transferRequestData // 이체의뢰서 정보 포함
    };
    const downloadFilename = filename || `급여이체의뢰서_${payYear}_${payMonth}_${paymentType}.xlsx`;
    return downloadExcel('transfer-request', params, downloadFilename);
}

export const downloadTransferExcel = (payYear, payMonth, paymentType, employeeNos, filename) => {
    /**
     * 급여 이체 엑셀 다운로드
     */
    const params = { payYear, payMonth, paymentType, employeeNos };
    const downloadFilename = filename || `급여이체_${payYear}_${payMonth}_${paymentType}.xlsx`;
    return downloadExcel('transfer', params, downloadFilename);
}

export const downloadLaborCostStatementExcel = (selectDate, filename) => {
    /**
     * 노무비내역서(일용직) 엑셀 다운로드 (API type: transfer-daily)
     * @param {Date} selectDate - 기준년월
     */
    const month = selectDate ? format(selectDate, 'yyyy-MM') : null;
    const year = selectDate ? format(selectDate, 'yyyy') : null;
    if (!month || !year) return Promise.reject(new Error('기준년월을 선택해주세요.'));
    const params = { month, year };
    const downloadFilename = filename || `노무비내역서_${year}_${month}.xlsx`;
    return downloadExcel('transfer-daily', params, downloadFilename);
}

export const downloadLeavePaymentExcel = async (payYear, payMonth, paymentType, filename) => {
    /**
     * 연차 지급대장 엑셀 다운로드
     */
    const params = { payYear, payMonth, paymentType };
    const downloadFilename = filename || `연차지급대장_${payYear}_${payMonth}_${paymentType}.xlsx`;

    try {
        const response = await downloadExcel('leave-payment', params, downloadFilename);
        return response;
    } catch (error) {
        console.error('연차 지급대장 엑셀 다운로드 중 오류:', error);
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '엑셀 다운로드 중 오류가 발생했습니다.'));
    }
}

export const TmpExcelDownload = async (type, params, filename) => {
    /**
     * 임시 엑셀 다운로드 서비스 (PDF 엔드포인트 사용)
     * type: 다운로드 타입
     * params: API 파라미터
     * filename: 다운로드 파일명
     */
    try {
        console.log('임시 엑셀 다운로드 요청:', {
            type: type,
            url: '/pdf/download',
            params: params,
            filename: filename
        });

        const response = await callAPI(ROUTES.PDF.DOWNLOAD, 'GET', {}, { type, ...params });

        console.log('임시 엑셀 다운로드 응답:', {
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
            contentType: response.headers['content-type']
        });

        // 응답이 에러인지 확인 (서버에서 JSON 에러 응답을 보낸 경우)
        if (response.headers['content-type']?.includes('application/json')) {
            // Blob을 JSON으로 변환하여 에러 메시지 확인
            const text = await response.data.text();
            const errorData = JSON.parse(text);
            if (errorData.error || errorData.message) {
                const error = new Error(errorData.message || errorData.error || '다운로드 중 오류가 발생했습니다.');
                error.response = { data: errorData, status: response.status };
                throw error;
            }
        }

        // 파일 다운로드 처리
        const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        
        // Blob 크기 확인 (너무 작으면 에러 응답일 가능성)
        if (blob.size < 100) {
            const text = await blob.text();
            console.warn('의심스러운 응답 크기:', blob.size, '내용:', text);
            try {
                const errorData = JSON.parse(text);
                if (errorData.error || errorData.message) {
                    const error = new Error(errorData.message || errorData.error || '다운로드 중 오류가 발생했습니다.');
                    error.response = { data: errorData, status: response.status };
                    throw error;
                }
            } catch (parseError) {
                // JSON 파싱 실패해도 계속 진행
            }
        }

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        console.log('임시 엑셀 파일 다운로드 완료:', filename);
        return { response, filename };
    } catch (error) {
        console.error('임시 엑셀 다운로드 오류:', {
            type: type,
            params: params,
            filename: filename,
            error: error,
            status: error.response?.status,
            statusText: error.response?.statusText,
            url: error.config?.url,
            responseData: error.response?.data
        });
        return Promise.reject(error instanceof Error ? error : new Error(error.message || '엑셀 다운로드 중 오류가 발생했습니다.'));
    }
}

